/**
 * In-memory key ring that holds symmetric channel keys indexed by epoch.
 *
 * Keys are never persisted — they are fetched from the server on cold start
 * and held only for the lifetime of the current session. This allows messages
 * from prior epochs to be decrypted after a key rotation without exposing
 * current-epoch keys to evicted members.
 */
export class EpochKeyRing {
    /** channelId → Map<epoch, key> */
    store = new Map();
    /**
     * Bumped by {@link clearAll}. Callers that decrypt a key across an `await`
     * capture this first and write back through {@link setIfGeneration}, so a
     * logout that lands mid-flight cannot have the previous user's key resurrected
     * into the ring after the wipe.
     */
    generation = 0;
    /**
     * channelId → bumped by {@link clear}. {@link generation} only covers a logout;
     * leaving or being removed from ONE channel clears just that channel, and a key
     * fetch already in flight for it must not write the key back afterwards (a
     * removed member who is later re-added without history would decrypt old epochs).
     */
    channelGenerations = new Map();
    /**
     * channelId → the epoch this device must hold before it may encrypt. Set
     * when the server (or a socket event) says the channel has moved past the
     * epoch in the ring: sending under the superseded key would let a removed
     * member read the ciphertext, because the API accepts any known epoch.
     * Satisfied automatically once a key at or above that epoch is stored.
     */
    required = new Map();
    listeners = new Set();
    /**
     * Call `listener` after every key write (any code path). Lets a UI that decrypts
     * from the ring notice keys cached elsewhere. Returns the unsubscribe function.
     */
    onSet(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    /** Session generation to capture before any await that ends in a `set`. */
    get currentGeneration() {
        return this.generation;
    }
    /** Per-channel generation to capture alongside {@link currentGeneration}. */
    channelGeneration(channelId) {
        return this.channelGenerations.get(channelId) ?? 0;
    }
    /**
     * Store a key for a given channel epoch.
     * Silently overwrites an existing entry for the same epoch.
     */
    set(channelId, epoch, key) {
        let epochs = this.store.get(channelId);
        if (!epochs) {
            epochs = new Map();
            this.store.set(channelId, epochs);
        }
        epochs.set(epoch, key);
        for (const listener of [...this.listeners])
            listener(channelId, epoch);
    }
    /** Record that sends must wait until this channel's key at `epoch` (or later) is held. */
    requireEpoch(channelId, epoch) {
        const current = this.required.get(channelId);
        if (current === undefined || epoch > current)
            this.required.set(channelId, epoch);
    }
    /**
     * True while a required epoch is known but its key is not in the ring, i.e.
     * the latest cached key is (or may be) superseded and must not be used to
     * encrypt. A channel with nothing required is never behind.
     */
    isBehind(channelId) {
        const need = this.required.get(channelId);
        if (need === undefined)
            return false;
        const latest = this.getLatest(channelId);
        if (latest && latest.epoch >= need) {
            this.required.delete(channelId);
            return false;
        }
        return true;
    }
    /**
     * Retrieve the key for a specific epoch, or undefined if not cached.
     */
    get(channelId, epoch) {
        return this.store.get(channelId)?.get(epoch);
    }
    /**
     * Return the key for the highest-numbered epoch in the ring for a channel,
     * or undefined if no keys are cached for that channel.
     */
    getLatest(channelId) {
        const epochs = this.store.get(channelId);
        if (!epochs || epochs.size === 0)
            return undefined;
        let latestEpoch = -1;
        for (const epoch of epochs.keys()) {
            if (epoch > latestEpoch)
                latestEpoch = epoch;
        }
        const key = epochs.get(latestEpoch);
        if (key === undefined)
            return undefined;
        return { epoch: latestEpoch, key };
    }
    /**
     * Remove a single epoch's key from the ring.
     * No-op if the epoch or channel is not present.
     */
    evict(channelId, epoch) {
        const epochs = this.store.get(channelId);
        if (!epochs)
            return;
        epochs.delete(epoch);
        if (epochs.size === 0)
            this.store.delete(channelId);
    }
    /**
     * Remove all cached keys for a channel.
     */
    clear(channelId) {
        this.store.delete(channelId);
        this.required.delete(channelId);
        // Invalidate any in-flight fetch for THIS channel that captured the old value.
        this.channelGenerations.set(channelId, this.channelGeneration(channelId) + 1);
    }
    /**
     * Wipe every channel's keys. Must be called on logout — the singleton
     * outlives the auth session, and a subsequent different-user login in the
     * same JS context would otherwise inherit the previous user's keys.
     */
    clearAll() {
        this.store.clear();
        this.required.clear();
        this.channelGenerations.clear();
        // Invalidate any in-flight decrypt that captured the prior generation.
        this.generation++;
    }
    /**
     * Store a key only if the session generation is still the one the caller
     * captured before its awaits. Returns false (and writes nothing) if a
     * {@link clearAll} intervened — i.e. the user logged out mid-fetch.
     *
     * Use this instead of {@link set} anywhere a key is decrypted across an
     * await; a bare `set` would resurrect the signed-out user's key.
     */
    setIfGeneration(channelId, epoch, key, generation, channelGeneration) {
        if (generation !== this.generation)
            return false;
        // When the caller captured the channel's generation too, a clear() of that
        // channel mid-flight (leave / removal) also refuses the write.
        if (channelGeneration !== undefined &&
            channelGeneration !== this.channelGeneration(channelId)) {
            return false;
        }
        this.set(channelId, epoch, key);
        return true;
    }
}
/** Singleton instance shared across the package. */
export const epochKeyRing = new EpochKeyRing();
//# sourceMappingURL=epoch-key-ring.js.map